'use client';
import { useState, useEffect, useMemo } from 'react';
import type { FilterGroup } from '@practium/ui';
import { mapListPageLineChart, mapListPagePieData } from '@practium/features/dashboard';
import type { UsersPermissions } from '../types';
import { useGetUsers } from '../../api/hooks/users/useGetUsers';
import { useGetUsersStats } from '../../api/hooks/users/useGetUsersStats';
import { useDebounce } from '../../../shared/utils/useDebounce';

const PAGE_SIZE = 10;

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Status',
		options: [
			{ value: 'status:true', label: 'Ativo' },
			{ value: 'status:false', label: 'Inativo' },
		],
	},
];

export interface UseUsersPageProps {
	permissions: UsersPermissions;
}

export const useUsersPage = ({ permissions }: UseUsersPageProps) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);

	const debouncedSearch = useDebounce(searchQuery, 400);
	const chartsQuery = useGetUsersStats();
	const chartsData = chartsQuery.data;

	const activeSelected = filterValues.includes('status:true');
	const inactiveSelected = filterValues.includes('status:false');
	const statusFilter = activeSelected === inactiveSelected ? null : activeSelected;
	const filtersKey = JSON.stringify([debouncedSearch, statusFilter]);
	const [paginationState, setPaginationState] = useState({ filtersKey, page: 1 });
	const page = paginationState.filtersKey === filtersKey ? paginationState.page : 1;
	const setPage = (nextPage: number) => setPaginationState({ filtersKey, page: nextPage });

	useEffect(() => {
		setPaginationState(current => current.filtersKey === filtersKey ? current : { filtersKey, page: 1 });
	}, [filtersKey]);

	const usersQuery = useGetUsers({
		page,
		limit: PAGE_SIZE,
		userNameOrEmail: debouncedSearch,
		status: statusFilter,
	});
	const filteredUsers = usersQuery.isPlaceholderData ? [] : usersQuery.data;
	const totalPages = usersQuery.isPlaceholderData
		? page
		: Math.max(1, usersQuery.pagination?.totalPages ?? 1);

	const profilesPieData = useMemo(
		() => mapListPagePieData(chartsData?.pie?.items),
		[chartsData]
	);

	const {
		lineData: insertionsLineData,
		mainValue: chartMainValue,
		trendValue: chartTrendValue,
		trendDirection: chartTrendDirection,
		footerText: insertionsFooterText,
	} = useMemo(() => mapListPageLineChart(chartsData?.line), [chartsData]);

	return {
		filteredUsers,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups: FILTER_GROUPS,
		page,
		setPage,
		totalPages,
		isTableLoading: usersQuery.isPending || usersQuery.isPlaceholderData,
		isTableFetching: usersQuery.isFetching,
		isTableError: usersQuery.isError,
		refetchTable: usersQuery.refetch,
		isChartsLoading: chartsQuery.isPending,
		isChartsFetching: chartsQuery.isFetching,
		isChartsError: chartsQuery.isError,
		permissions,
		profilesPieData,
		insertionsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		insertionsFooterText,
	};
};
